// What an admit-card QR can contain, and how the verifier page should use it.
//
// - The verification link the backend prints (qrService.buildVerificationUrl):
//     {VERIFICATION_BASE_URL}/candidate-verification?data=<sealed_payload>
//   Only the sealed `data` value is kept. The scanned link itself is never
//   opened, so a QR pointing at another site cannot send the verifier there;
//   whatever `data` it carries is still checked by the backend (tamper check).
// - A bare sealed payload (base64url of iv | auth tag | ciphertext, so always
//   well over 40 characters).
// - Just the roll number, which the backend prints when the link cannot be
//   built; it is looked up like a typed roll number.
// - The reference admit card's public link {base}/r/<rollno>.<k>.<sig> (or the
//   first form {base}/roll-number/<rollno>?k=&s=); only the roll number is kept
//   and looked up like a typed one.
import { readRollNoLink } from "../../utils/rollNoQrKeys";

export const SCAN_TYPE = {
  SEALED: "sealed",
  ROLL_NO: "rollno",
  INVALID: "invalid",
};

const SEALED_PAYLOAD = /^[A-Za-z0-9_-]{40,}$/;
const ROLL_NO = /^[A-Za-z0-9/-]{1,30}$/;

export const parseScannedQr = (text) => {
  const value = (text || "").trim();

  if (/^https?:\/\//i.test(value)) {
    try {
      const url = new URL(value);
      const data = url.searchParams.get("data");
      if (url.pathname.replace(/\/+$/, "").endsWith("/candidate-verification") && data) {
        return { type: SCAN_TYPE.SEALED, value: data };
      }
      // Reference admit card: /r/<rollno>.<k>.<sig> or /roll-number/<rollno>?k=&s=
      // — looked up like a typed roll number (the lookup checks it against the
      // database, so the link's signature is not needed here).
      const link = readRollNoLink(url.pathname, url.searchParams);
      if (link && ROLL_NO.test(link.rollno)) return { type: SCAN_TYPE.ROLL_NO, value: link.rollno };
    } catch (e) {
      // Not a usable link; reported as invalid below.
    }
    return { type: SCAN_TYPE.INVALID };
  }

  if (SEALED_PAYLOAD.test(value)) {
    return { type: SCAN_TYPE.SEALED, value };
  }
  if (ROLL_NO.test(value)) {
    return { type: SCAN_TYPE.ROLL_NO, value };
  }
  return { type: SCAN_TYPE.INVALID };
};
